import type { CreateInquiryRequest, InquirySuccessResponse } from '../types';
import { validateInquiry, hasErrors } from '../utils/validation';
import { getApartmentById, isValidApartmentId } from '../data/apartments';

class InquirySubmissionError extends Error {
  status: number;
  details: unknown;

  constructor(status: number, message: string, details: unknown = []) {
    super(message);
    this.name = 'InquirySubmissionError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Submit inquiry through a local SMTP bridge (Nodemailer-based).
 * Falls back to local storage if SMTP bridge is unavailable.
 */
export async function submitInquiry(inquiry: CreateInquiryRequest): Promise<InquirySuccessResponse> {
  // Validate locally first
  const validationErrors = validateInquiry(inquiry);
  if (hasErrors(validationErrors)) {
    throw new InquirySubmissionError(400, 'Invalid inquiry data', validationErrors);
  }

  if (!isValidApartmentId(inquiry.apartmentId)) {
    throw new InquirySubmissionError(
      404,
      `Apartment with ID ${inquiry.apartmentId} not found`,
      { apartmentId: 'validation.apartmentNotFound' },
    );
  }

  const smtpBridgeUrl = import.meta.env.VITE_SMTP_BRIDGE_URL?.trim();
  if (!smtpBridgeUrl) {
    throw new InquirySubmissionError(
      503,
      'Inquiry service is unavailable because VITE_SMTP_BRIDGE_URL is not configured.',
      { smtpBridgeUrl: 'missing' },
    );
  }

  try {
    const bridgeResult = await sendInquiryViaSmtpBridge(inquiry, smtpBridgeUrl);
    const warning = getBridgeWarning(bridgeResult.details);

    return {
      message: bridgeResult.message || 'Inquiry processed successfully. We will contact you shortly.',
      inquiryId: Date.now(),
      warning,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Inquiry submission failed';
    console.error('SMTP bridge send failed.', error);
    throw new InquirySubmissionError(502, message, error);
  }
}

interface SmtpBridgeResponse {
  success?: boolean;
  message?: string;
  details?: unknown;
}

async function sendInquiryViaSmtpBridge(
  inquiry: CreateInquiryRequest,
  smtpBridgeUrl: string,
): Promise<SmtpBridgeResponse> {
  const response = await fetch(smtpBridgeUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(buildTemplateParams(inquiry)),
  });

  if (!response.ok) {
    const responseText = await response.text();
    throw new Error(`SMTP bridge request failed with status ${response.status}. Body: ${responseText}`);
  }

  const result = (await response.json()) as SmtpBridgeResponse;
  if (!result.success) {
    const details = result.details ? ` Details: ${JSON.stringify(result.details)}` : '';
    throw new Error((result.message || 'SMTP bridge failed to send inquiry') + details);
  }

  return result;
}

function getBridgeWarning(details: unknown): string | undefined {
  if (!details || typeof details !== 'object') {
    return undefined;
  }

  const warning = (details as { warning?: unknown }).warning;
  return typeof warning === 'string' && warning.trim() ? warning : undefined;
}

function buildTemplateParams(inquiry: CreateInquiryRequest): Record<string, string | number> {
  const apartmentName = getApartmentById(inquiry.apartmentId)?.name ?? `Apartment ${inquiry.apartmentId}`;

  return {
    apartment_id: inquiry.apartmentId,
    apartment_name: apartmentName,
    guest_name: inquiry.guestName.trim(),
    guest_email: inquiry.guestEmail.trim(),
    guest_phone: inquiry.guestPhone?.trim() || 'N/A',
    check_in_date: inquiry.checkInDate,
    check_out_date: inquiry.checkOutDate,
    num_adults: inquiry.numAdults,
    num_children: inquiry.numChildren,
    message: inquiry.message?.trim() || 'N/A',
    submitted_at: new Date().toISOString(),
  };
}
